<template>
  <div class="operator-card">
    <div class="operator-card-image-container flex">
      <img class="operator-image" :src="operator.featured_media" alt="" />
      <button
        @click="handleViewOnMap"
        class="view-on-map figtree flex rounded-full"
        aria-label="View on map"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 36.21 52.82"
          fill="#27201F"
          class="view-on-map-icon"
        >
          <path
            d="M31.74 4.99C26.11-.67 15.41-1.25 8.39 1.92.09 5.66-1.46 15.35 1.18 23.29c1.65 5 4.46 9.61 7.06 14.18 2.73 4.81 5.51 9.59 8.2 14.44.56.99 2.15 1.27 2.94.37.56-.66 1.03-1.35 1.44-2.08.13-.15.24-.32.34-.54.09-.24.21-.49.32-.71a.21.21 0 0 0 .07-.13c.19-.39.39-.82.58-1.24 3.73-7.47 9.65-13.73 12.47-21.63 2.43-6.78 2.53-15.53-2.87-20.96Zm-6.23 13.46c-.72 2.96-2.98 5.72-6.07 6.34-.92.18-1.83.16-2.69-.06-1.44-.06-2.86-.67-3.96-1.74-1.68-1.64-2.56-4.14-2.46-6.47.11-2.56 1.25-4.82 3.29-6.35 1.14-.86 2.57-1.31 4.01-1.41 1.18-.28 2.42-.23 3.5.2.65.11 1.29.3 1.92.65 3.04 1.73 3.19 5.78 2.45 8.82Z"
          />
        </svg>
        <span class="view-on-map-label"> View on map </span>
      </button>
    </div>
    <div class="operator-card-details-container">
      <div class="flex justify-between items-start">
        <h3
          class="line-clamp-2 leading-tight operator-title barlow flex-grow"
          :title="operator.title.rendered"
          v-html="operator.title.rendered"
        ></h3>
      </div>
      <div class="flex flex-col sm:flex-row gap-2">
        <a :href="operator.link" target="_blank" class="view-details rounded-full figtree">
          View details
        </a>
        <TripPlannerButton :id="operator.id" />
      </div>
    </div>
    <div class="reorder-buttons ml-2">
      <div class="marker-index barlow">{{ markerIndex }}</div>
      <button
        @click="moveOperator('up')"
        class="reorder-btn"
        :disabled="markerIndex === 1"
        aria-label="Move up"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="w-4 h-4"
        >
          <path d="M12 19V5M5 12l7-7 7 7" />
        </svg>
      </button>
      <button
        @click="moveOperator('down')"
        class="reorder-btn"
        :disabled="isLastOperator"
        aria-label="Move down"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="w-4 h-4"
        >
          <path d="M12 5v14M5 12l7 7 7-7" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import TripPlannerButton from "./TripPlannerButtonTransition.vue";

// Constants
const LOCAL_STORAGE_KEY = "trip_planner_ids";

// Props
const props = defineProps({
  operator: {
    type: Object,
    required: true,
  },
  markerIndex: {
    type: Number,
    required: true,
  },
});

// Emits
const emit = defineEmits(["view-on-map", "reorder"]);

// Computed properties
const isLastOperator = computed(() => {
  const tripPlannerIds = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || "[]");
  return props.markerIndex === tripPlannerIds.length;
});

// Methods
const handleViewOnMap = () => {
  emit("view-on-map", props.operator);
};

const moveOperator = (direction) => {
  const tripPlannerIds = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || "[]");
  const currentIndex = tripPlannerIds.indexOf(props.operator.id);

  if (currentIndex === -1) return;

  const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

  if (newIndex >= 0 && newIndex < tripPlannerIds.length) {
    // Swap elements
    [tripPlannerIds[currentIndex], tripPlannerIds[newIndex]] = [
      tripPlannerIds[newIndex],
      tripPlannerIds[currentIndex],
    ];

    // Save back to localStorage
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(tripPlannerIds));

    // Emit event with the new order
    emit("reorder", tripPlannerIds);

    // Dispatch a custom event that App.vue can listen for
    window.dispatchEvent(
      new CustomEvent("tripPlannerReorder", {
        detail: { ids: tripPlannerIds },
      }),
    );
  }
};
</script>

<style scoped>
.operator-card {
  font-size: 20px;
  background: white;
  border-radius: 20px;
  padding: 10px;
  position: relative;
  display: flex;
}

.operator-card-image-container {
  flex-basis: 150px;
  margin-right: 20px;
  position: relative;

  @media screen and (max-width: 400px) {
    display: none;
  }
}

.operator-image {
  /* width: 100%; */
  width: 150px;
  height: 120px;
  object-fit: cover;
  object-position: center;
  border-radius: 10px;
}

.operator-card-details-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.operator-title {
  font-size: 20px;
  text-transform: initial;
  color: #27201f;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  line-height: 1.2em;
  min-height: 2.2em;
  margin-top: 10px;
}

.view-on-map {
  background: white;
  padding: 0.5em 1em 0.5em 0.5em;
  font-size: 9px;
  position: absolute;
  top: 10px;
  left: 10px;
  border: 0;
  box-shadow: 0 0 5px #0000001f;
}

.view-on-map-icon {
  width: 8px;
  margin-right: 5px;
  margin-left: 3px;
}

.view-details {
  font-size: 10px;
  border: 1.5px solid var(--vwc-color-border);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: inherit;
  text-decoration: none;
  padding: 1em;
}

.view-details:hover {
  background: var(--vwc-color-primary);
  color: white;
  border-color: var(--vwc-color-primary);
}

.marker-index {
  /* position: absolute; */
  /* bottom: 10px; */
  /* left: 10px; */
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  border: 1.5px solid var(--vwc-color-border);
  z-index: 1;
  background: var(--vwc-color-border);
  color: white;
  margin-bottom: 6px;
}

.reorder-buttons {
  display: flex;
  flex-direction: column;
  margin-left: 10px;
  gap: 4px;
}

.reorder-btn {
  width: 35px;
  padding: 6px;
  background: transparent;
  border: 1.5px solid var(--vwc-color-border);
  border-radius: 9999px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #27201f;
  transition: all 0.2s;
}

.reorder-btn:hover:not(:disabled) {
  background: var(--vwc-color-border);
  color: white;
}

.reorder-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
